import * as React from "react";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  BurnList,
  BurnNonSensitiveUi,
  BurnTable,
  BurnTemperatureSeriesNonSensitive,
  BurnTemperatureSeriesNonSensitiveImpl,
  BurnTimeSeriesAnonymizedChart,
  FlueBurnMetricAggregates,
  OrganizationDto,
  SootingDegreeCalculationMethod,
} from "firefly-shared-ts-domain";
import {
  Box,
  CircularProgress,
  Paper,
  Stack,
  SxProps,
  Toolbar,
  Typography,
} from "@mui/material";
import { Button } from "shared-ts-mui";
import {
  PaneDimensionsAndSizeProvider,
  SizeClass,
  useDense,
  usePaneSize,
  usePaneWidthComparer,
  useResponsive,
} from "shared-ts-ui-responsive";
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import IndeterminateCheckBoxRoundedIcon from "@mui/icons-material/IndeterminateCheckBoxRounded";
import { useTranslation } from "react-i18next";
import { BurnInfoMultiple } from "./BurnInfoMultiple";
import { BurnInfoSingle } from "./BurnInfoSingle";
import { DateTime, Duration } from "luxon";
import { useSelected } from "./useSelected";
import { useSelectedBurnTemperatureSeries } from "./useSelectedBurnTemperatureSeries";
import { useBurnColors } from "./useBurnColors";
import { notUndef } from "shared-ts-utils-core";
import { useHotkeys } from "react-hotkeys-hook";
import { Key } from "ts-key-enum";
import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelGroupProps,
} from "react-resizable-panels";
import { QueryResultMetaData } from "shared-ts-ui-loading";
import { ResizeHandle } from "shared-ts-ui-react-resizeable-panels";
import { useResizeDetector } from "react-resize-detector";
import {
  IntSetting,
  useUserSettingWithDefault,
} from "shared-ts-utils-user-settings";

const miniumMaxDuration = Duration.fromObject({ hours: 4 });
export type BurnsAndTemperatureCurvesPanelViewMode =
  | "listSingle"
  | "listMulti"
  | "table";
export interface BurnsAndTemperatureCurvesPanelProps {
  viewMode: BurnsAndTemperatureCurvesPanelViewMode;
  queryResultMetaData?: QueryResultMetaData;
  noDataText?: string;
  organization: OrganizationDto;
  flueSootingDegreeCalculationMethod: SootingDegreeCalculationMethod;
  flueBurnMetricAggregates: FlueBurnMetricAggregates;
  burns?: Array<BurnNonSensitiveUi>;
  burnTemperatureSeries?: Array<BurnTemperatureSeriesNonSensitive>;
  selectedMonth: DateTime;
  chimneyFireThreshold?: number;
  layout: SizeClass;
  sx?: SxProps;
}

export const BurnsAndTemperatureCurvesPanel: FC<
  BurnsAndTemperatureCurvesPanelProps
> = (props) => {
  // Props-based
  const viewMode = props.viewMode;

  const burns = props.burns;
  const burnTemperatureSeries = props.burnTemperatureSeries;

  // State-based
  const [selectedBurnIndexes, setSelectedBurnIndexes] = useState<
    Array<boolean> | undefined
  >(props.burns?.map(() => false) ?? undefined);

  // External/context hooks based
  const { t } = useTranslation("firefly-chimney-insights-ts-domain");
  const dense = useDense();
  const {
    width: widthInPx,
    height: heightInPx,
    ref: rootRef,
  } = useResizeDetector();
  const {
    width: leftWidthInPx,
    height: leftHeightInPx,
    ref: leftRef,
  } = useResizeDetector();
  const {
    width: rightWidthInPx,
    height: rightHeightInPx,
    ref: rightRef,
  } = useResizeDetector();

  const responsive = useResponsive();
  const widthComparer = usePaneWidthComparer();
  const { width } = usePaneSize();
  // Derived
  const listPanelImperativeRef = useRef<ImperativePanelHandle>(null);
  const { everySelected, someSelected, selectionCount, noneSelected } =
    useSelected(selectedBurnIndexes);
  const multiSelect =
    viewMode === "listMulti" || (viewMode === "table" && selectionCount > 1);
  const burnColors = useBurnColors(burns);
  const panelDirection: PanelGroupProps["direction"] = widthComparer.lte(
    SizeClass.Medium
  )
    ? "vertical"
    : "horizontal";

  const listPanelMinSizeInPx = viewMode.startsWith("list")
    ? widthComparer.lte(SizeClass.Medium)
      ? 57
      : 200
    : 200;

  const listPanelMinSize =
    panelDirection === "horizontal"
      ? notUndef(widthInPx, (it) =>
          it > 0 ? (listPanelMinSizeInPx * 100) / it : undefined
        )
      : notUndef(heightInPx, (it) =>
          it > 0 ? (listPanelMinSizeInPx * 100) / it : undefined
        );
  const listPanelDefaultSizeInPx = notUndef(widthInPx, (it) =>
    viewMode.startsWith("list")
      ? widthComparer.lte(SizeClass.Medium)
        ? 57
        : 200
      : it / 2
  );
  const listPanelDefaultSize =
    panelDirection === "horizontal"
      ? notUndef(widthInPx, (widthInPxNotUndef) =>
          notUndef(
            listPanelDefaultSizeInPx,
            (listPanelDefaultSizeInPxNotUndef) =>
              widthInPxNotUndef > 0
                ? (listPanelDefaultSizeInPxNotUndef * 100) / widthInPxNotUndef
                : undefined
          )
        )
      : notUndef(heightInPx, (heightInPxNotUndef) =>
          notUndef(
            listPanelDefaultSizeInPx,
            (listPanelDefaultSizeInPxNotUndef) =>
              heightInPxNotUndef > 0
                ? (listPanelDefaultSizeInPxNotUndef * 100) / heightInPxNotUndef
                : undefined
          )
        );
  const listPanelMaxSize = viewMode.startsWith("list")
    ? listPanelDefaultSize
    : 100;

  const chartPanelMinSize = viewMode.startsWith("list")
    ? 100 - (listPanelMinSize ?? 0)
    : 50;

  const [tablePanelSize, setTablePanelSize] = useUserSettingWithDefault(
    "BurnsAndTemperatureCurvesPanel.tablePanelSize",
    IntSetting,
    50,
    { storeLocally: true }
  );

  const {
    selectedBurns,
    selectedBurn,
    selectedBurnEvents,
    selectedBurnTemperatureSeries,
    selectedBurnColors,
    selectedBurnDisplayNames,
  } = useSelectedBurnTemperatureSeries(
    burns,
    burnTemperatureSeries,
    burnColors,
    selectedBurnIndexes
  );
  const maxDuration = useMemo(() => {
    return (
      notUndef(burnTemperatureSeries, (it) =>
        BurnTemperatureSeriesNonSensitiveImpl.getLongestDuration(it)
      ) ?? miniumMaxDuration
    );
  }, [burnTemperatureSeries]);

  // Effects
  useEffect(
    function syncSelectionWithMultiSelect() {
      setSelectedBurnIndexes((prev) => {
        if (!prev) return prev;
        // If single-select, collapse to at most one "true"
        if (!multiSelect) {
          const firstSelected = prev.findIndex((v) => v);
          const keepIndex = firstSelected >= 0 ? firstSelected : 0;
          return prev.map((_, i) => i === keepIndex);
        }
        // If multi-select, keep current state
        return prev;
      });
    },
    [multiSelect]
  );

  useEffect(
    function syncStateWithBurns() {
      if (props.burns !== undefined) {
        const selectedBurnIndexes =
          props.burns?.map((_, index) => index === 0) ?? undefined;
        setSelectedBurnIndexes(selectedBurnIndexes);
      } else {
        setSelectedBurnIndexes(undefined);
      }
    },
    [props.burns]
  );

  useEffect(
    function syncListPanelSize() {
      if (viewMode === "table") {
        listPanelImperativeRef.current?.resize(tablePanelSize);
      } else if (viewMode === "listSingle" || viewMode === "listMulti") {
        if (listPanelDefaultSize != null) {
          listPanelImperativeRef.current?.resize(listPanelDefaultSize);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [viewMode]
  );

  const handleFirstPanelResize = (size: number) => {
    if (viewMode === "table") {
      setTablePanelSize(size);
    }
  };

  const handleBurnsSelected = (value: Array<boolean>) => {
    setSelectedBurnIndexes(value);
  };

  const handleSelectionToggle = () => {
    if (everySelected) {
      setSelectedBurnIndexes((prevState) => prevState?.map(() => false));
    } else if (someSelected || noneSelected) {
      setSelectedBurnIndexes((prevState) => prevState?.map(() => true));
    }
  };

  useHotkeys(Key.ArrowUp, () => {
    if (multiSelect) {
      // not suppoerted
    } else {
      setSelectedBurnIndexes((prevState) => {
        if (!prevState) return prevState;
        const currentIndex = prevState.findIndex((v) => v);
        if (currentIndex <= 0) return prevState;
        return prevState.map((_, i) => i === currentIndex - 1);
      });
    }
  });

  useHotkeys(Key.ArrowDown, () => {
    if (multiSelect) {
      // not suppoerted
    } else {
      setSelectedBurnIndexes((prevState) => {
        if (!prevState) return prevState;
        const currentIndex = prevState.findIndex((v) => v);
        if (currentIndex >= prevState.length - 1) return prevState;
        return prevState.map((_, i) => i === currentIndex + 1);
      });
    }
  });

  const selectionIcon = everySelected ? (
    <CheckBoxRoundedIcon />
  ) : someSelected ? (
    <IndeterminateCheckBoxRoundedIcon />
  ) : (
    <CheckBoxOutlineBlankRoundedIcon />
  );

  return (
    <Box
      sx={props.sx}
      ref={rootRef}
      className={"BurnsAndTemperatureCurvesPanel"}
    >
      {listPanelMinSize != null && (
        <PanelGroup direction={panelDirection} style={{ minHeight: 0 }}>
          <Panel
            ref={listPanelImperativeRef}
            minSize={listPanelMinSize}
            defaultSize={listPanelDefaultSize}
            maxSize={listPanelMaxSize}
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
            onResize={handleFirstPanelResize}
          >
            <Box
              className={"BurnsAndTemperatureCurvesPanel-listPanel-container"}
              ref={leftRef}
              sx={{
                flexGrow: 1,
                minHeight: 0,
                minWidth: 0,
                display: "flex",
                flexDirection:
                  panelDirection === "horizontal"
                    ? "column"
                    : widthComparer.lte(SizeClass.Medium)
                    ? "row"
                    : "column",
              }}
            >
              <PaneDimensionsAndSizeProvider
                width={leftWidthInPx}
                height={leftHeightInPx}
              >
                {viewMode === "listMulti" && (
                  <Toolbar
                    {...responsive.toolbar}
                    sx={{
                      pl: 1.4,
                      minHeight: 0,
                    }}
                  >
                    {(burns?.length ?? 0) > 0 && (
                      <Button
                        color={"inherit"}
                        onClick={handleSelectionToggle}
                        startIcon={selectionIcon}
                        disabled={!multiSelect}
                        sx={{ opacity: multiSelect ? 1 : 0 }}
                      />
                    )}
                  </Toolbar>
                )}
                {(viewMode === "listSingle" || viewMode === "listMulti") && (
                  <BurnList
                    burns={burns}
                    multiSelect={multiSelect}
                    selected={selectedBurnIndexes}
                    hideMarkers={!multiSelect}
                    onSelected={handleBurnsSelected}
                    direction={
                      widthComparer.lte(SizeClass.Medium) ? "row" : "column"
                    }
                    dense={dense}
                    layout={SizeClass.ExtraLarge}
                    sx={{ overflowY: "auto", minHeight: 0 }}
                  />
                )}
                {viewMode === "table" && (
                  <Stack sx={{ flexGrow: 1, minHeight: 1, minWidth: 0, p: 1 }}>
                    <BurnTable
                      organization={props.organization}
                      flueSootingDegreeCalculationMethod={
                        props.flueSootingDegreeCalculationMethod
                      }
                      burns={burns}
                      selected={selectedBurnIndexes}
                      onSelected={handleBurnsSelected}
                    />
                  </Stack>
                )}
              </PaneDimensionsAndSizeProvider>
            </Box>
          </Panel>
          <ResizeHandle
            width={8}
            direction={panelDirection}
            disabled={listPanelMinSize === listPanelMaxSize}
          />
          <Panel
            minSize={chartPanelMinSize}
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <Box
              ref={rightRef}
              sx={{
                flexGrow: 1,
                minHeight: 0,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <PaneDimensionsAndSizeProvider
                width={rightWidthInPx}
                height={rightHeightInPx}
              >
                {viewMode !== "table" && (
                  <Toolbar {...responsive.toolbar}>
                    {(burns?.length ?? 0) > 0 && noneSelected && (
                      <Typography color={"text.secondary"}>
                        Klikk for å velge en fyring
                      </Typography>
                    )}
                    {!multiSelect && selectedBurn != null && (
                      <BurnInfoSingle
                        burn={selectedBurn}
                        layout={widthComparer.sizeClass}
                      />
                    )}
                    {multiSelect &&
                      selectedBurns != null &&
                      selectedBurns.length > 0 && (
                        <BurnInfoMultiple burns={selectedBurns} />
                      )}
                  </Toolbar>
                )}
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {props.burnTemperatureSeries === undefined && (
                    <Box
                      sx={{
                        flexGrow: 1,
                        minWidth: 0,
                        minHeight: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  )}
                  {props.burnTemperatureSeries?.length === 0 && (
                    <Box
                      sx={{
                        flexGrow: 1,
                        minWidth: 0,
                        minHeight: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography>
                        {props.noDataText ?? t("No burns")}
                      </Typography>
                    </Box>
                  )}
                  {(props.burnTemperatureSeries?.length ?? 0) > 0 &&
                    selectedBurnTemperatureSeries !== undefined &&
                    selectedBurnTemperatureSeries.length === 0 && (
                      <Box
                        sx={{
                          flexGrow: 1,
                          minWidth: 0,
                          minHeight: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography>
                          {props.noDataText ?? t("No burns chosen")}
                        </Typography>
                      </Box>
                    )}
                  {selectedBurnTemperatureSeries !== undefined &&
                    selectedBurnTemperatureSeries.length > 0 && (
                      <BurnTimeSeriesAnonymizedChart
                        flueBurnMetricAggregates={
                          props.flueBurnMetricAggregates
                        }
                        burnTimeSeries={selectedBurnTemperatureSeries}
                        burnEvents={
                          viewMode === "table" ? selectedBurnEvents : undefined
                        }
                        burnColors={selectedBurnColors}
                        burnDisplayNames={selectedBurnDisplayNames}
                        selectedMonth={props.selectedMonth}
                        multiSelect={multiSelect}
                        timeAxis={{
                          zoom: true,
                          minimumMaxDuration: miniumMaxDuration,
                          maxDuration: maxDuration,
                        }}
                        layout={width}
                        sx={{ flexGrow: 1, minHeight: "0px", minWidth: 0 }}
                      />
                    )}
                </Paper>
              </PaneDimensionsAndSizeProvider>
            </Box>
          </Panel>
        </PanelGroup>
      )}
    </Box>
  );
};
